"use client"

import * as React from "react"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"

import { cn } from "@/lib/utils"
import { XIcon } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"

const DialogSizeContext = React.createContext<DialogVariants["size"]>("md")

const dialogVariants = cva(
  "fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 duration-200 outline-none",
  {
    variants: {
      size: {
        sm: "sm:max-w-sm p-4 rounded-xl",
        md: "sm:max-w-md p-5 rounded-xl",
        lg: "sm:max-w-lg p-6 rounded-2xl",
        xl: "sm:max-w-xl p-6 rounded-2xl",
        "2xl": "sm:max-w-2xl p-6 rounded-2xl",
        "4xl": "sm:max-w-4xl p-6 rounded-2xl",
        "6xl": "sm:max-w-6xl p-6 rounded-2xl",
        full: "sm:max-w-4xl p-6 rounded-2xl",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

export type DialogVariants = VariantProps<typeof dialogVariants>

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        // Positioning
        "fixed inset-0 isolate z-50",
        // Glass overlay effect
        "bg-black/20 dark:bg-black/40 backdrop-blur-sm",
        // Animation
        "duration-200",
        "data-open:animate-in data-open:fade-in-0",
        "data-closed:animate-out data-closed:fade-out-0",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  closeLabel = "Close",
  size = "md",
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
  closeLabel?: string
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "4xl" | "6xl" | "full"
}) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogSizeContext.Provider value={size}>
        <DialogPrimitive.Popup
          data-slot="dialog-content"
          className={cn(
            // Base positioning and layout from CVA
            dialogVariants({ size }),
            // Glass morphism background
            "bg-card backdrop-blur-xl",
            // Glass border
            "border border-surface",
            // Shadow for depth
            "shadow-card",
            // Text styling
            "text-sm text-foreground",
            // Mobile scroll optimization
            "overscroll-contain",
            "[&::-webkit-overflow-scrolling:touch]",
            // Animation states
            "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
            "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            // Slide animations based on direction (for future use)
            "data-[side=bottom]:slide-in-from-top-2",
            "data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2",
            "data-[side=top]:slide-in-from-bottom-2",
            "glass",
            className
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className={cn(
                "absolute right-3 top-3",
                "rounded-lg p-1.5",
                // Glass button effect
                "bg-overlay/50",
                "hover:bg-overlay/80",
                "transition-all duration-150",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                "disabled:pointer-events-none disabled:opacity-50"
              )}
            >
              <XIcon className="h-4 w-4 text-muted-foreground" />
              <span className="sr-only">{closeLabel}</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Popup>
      </DialogSizeContext.Provider>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex flex-col gap-1.5",
        "text-left",
        className
      )}
      {...props}
    />
  )
}

const dialogFooterVariants = cva(
  "flex flex-col-reverse gap-1.5 sm:flex-row sm:justify-end sm:gap-3 py-3 px-4 rounded-b-lg border-t border-border bg-muted/50",
  {
    variants: {
      size: {
        sm: "-mx-4 -mb-4 px-4",   // matches p-4
        md: "-mx-5 -mb-5 px-5",   // matches p-5
        lg: "-mx-6 -mb-6 px-6",   // matches p-6
        xl: "-mx-6 -mb-6 px-6",   // matches p-6
        "2xl": "-mx-6 -mb-6 px-6", // matches p-6
        "4xl": "-mx-6 -mb-6 px-6", // matches p-6
        "6xl": "-mx-6 -mb-6 px-6", // matches p-6
        full: "-mx-6 -mb-6 px-6", // matches p-6
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
)

function DialogFooter({
  className,
  showCloseButton = false,
  closeLabel = 'Close',
  size: sizeProp,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
  closeLabel?: string
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "4xl" | "6xl" | "full"
}) {
  const contextSize = React.useContext(DialogSizeContext)
  const size = sizeProp ?? contextSize
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        dialogFooterVariants({ size }),
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          className={cn(
            "inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm flex-1 sm:flex-none",
            "bg-overlay/50",
            "border border-border",
            "hover:bg-overlay/80",
            "transition-all duration-150"
          )}
        >
          {closeLabel}
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "text-lg font-semibold leading-none tracking-tight",
        "text-foreground",
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-sm text-muted-foreground",
        "leading-relaxed",
        // Link styling
        "*:[a]:underline *:[a]:underline-offset-4 *:[a]:text-primary dark:*:[a]:text-primary",
        "*:[a]:hover:text-primary dark:*:[a]:hover:text-primary",
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  dialogVariants,
}
